import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { DEFAULT_LANG } from '../config/locale';

interface FooterProps {
  lang?: 'ko' | 'en';
}

const Footer: React.FC<FooterProps> = ({ lang = DEFAULT_LANG }) => {
  const isKo = lang === 'ko';

  return (
    <Wrapper>
      <Inner>
        <TopRow $cols={isKo ? 3 : 2}>
          <InfoSection>
            <CompanyName>
              <LogoImg src={isKo ? "/logo.jpg" : "/logo-en.svg"} alt={isKo ? "우수건설기계" : "Woosu Machinery Co. Ltd"} />
            </CompanyName>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>{isKo ? '대표이사' : 'CEO'}</InfoLabel>
                <InfoValue>{isKo ? '황창언' : 'Hwang Chang-eon'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{isKo ? '사업자등록번호' : 'Business Reg. No.'}</InfoLabel>
                <InfoValue>710-81-01949</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{isKo ? '통신판매업신고' : 'E-Commerce Reg.'}</InfoLabel>
                <InfoValue>2022-{isKo ? '광주서구' : 'Seo-gu, Gwangju'}-0526</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{isKo ? '주소' : 'Address'}</InfoLabel>
                <InfoValue>
                  {isKo
                    ? '광주광역시 서구 하남대로502번길 20-11 (덕흥동)'
                    : '20-11, Hanam-daero 502beon-gil, Seo-gu, Gwangju, Korea'}
                </InfoValue>
              </InfoItem>
            </InfoGrid>
          </InfoSection>

          <ContactSection>
            <ContactTitle>{isKo ? '고객센터' : 'Contact'}</ContactTitle>
            <PhoneNumber>{isKo ? '1522-1609' : '+82-1522-1609'}</PhoneNumber>
            <ContactDetail>
              <span>{isKo ? '휴대폰' : 'Mobile'}: {isKo ? '010-3245-3571' : '+82-10-3245-3571'}</span>
              <span>FAX: {isKo ? '062-523-1609' : '+82-62-523-1609'}</span>
            </ContactDetail>
            <ContactDetail>
              <span>{isKo ? '상담시간' : 'Hours'}: {isKo ? '월~토 08:00~20:00' : 'Mon-Sat 08:00-20:00'}</span>
            </ContactDetail>
            <ContactDetail>
              <span>{isKo ? '이메일' : 'Email'}: top35789@naver.com</span>
            </ContactDetail>
            <ContactDetail>
              <span>{isKo ? '카카오톡' : 'KakaoTalk'}: top094989</span>
            </ContactDetail>
          </ContactSection>

          {/* 2026-09-02 영문 전용 전환 때 삭제. 한국어 화면에만 되살린다. */}
          {isKo && (
            <BankSection>
              <ContactTitle>계좌안내</ContactTitle>
              <BankName>하나은행</BankName>
              <BankAccount>856-910025-72105</BankAccount>
              <BankHolder>예금주: 주식회사 우수건설기계</BankHolder>
            </BankSection>
          )}
        </TopRow>

        <Divider />

        <BottomRow>
          <Copyright>
            Copyright © 2011 <strong>{isKo ? '주식회사 우수건설기계' : 'Woosu Construction Machinery Co., Ltd.'}</strong> All rights reserved.
          </Copyright>
        </BottomRow>
      </Inner>
    </Wrapper>
  );
};

const Wrapper = styled.footer`
  background: #1A1A2E;
  color: rgba(255, 255, 255, 0.7);
  padding: 48px 0 32px;
  margin-top: auto;
`;

const Inner = styled.div`
  max-width: ${theme.layout.maxWidth};
  margin: 0 auto;
  padding: 0 40px;
`;

const TopRow = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: ${({ $cols }) => ($cols === 3 ? '2fr 1fr 1fr' : '2fr 1fr')};
  gap: 40px;
`;

const InfoSection = styled.div``;

const CompanyName = styled.div`
  margin-bottom: 20px;
`;

const LogoImg = styled.img`
  height: 32px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  opacity: 0.9;
`;

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoItem = styled.div`
  display: flex;
  gap: 8px;
  font-size: 13px;
  line-height: 1.5;
`;

const InfoLabel = styled.span`
  color: rgba(255, 255, 255, 0.4);
  white-space: nowrap;
  min-width: 100px;
`;

const InfoValue = styled.span`
  color: rgba(255, 255, 255, 0.7);
`;

const ContactSection = styled.div``;

const ContactTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 12px;
`;

const PhoneNumber = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: 10px;
`;

const ContactDetail = styled.div`
  font-size: 13px;
  line-height: 1.8;
  display: flex;
  flex-direction: column;
`;

const BankSection = styled.div``;

const BankName = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 4px;
`;

const BankAccount = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 6px;
`;

const BankHolder = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin: 32px 0 20px;
`;

const BottomRow = styled.div`
  display: flex;
  justify-content: center;
`;

const Copyright = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  strong { color: rgba(255, 255, 255, 0.5); }
`;

export default Footer;
